import { AsyncPipe } from '@angular/common'
import { Component } from '@angular/core'
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms'
import { RouterLink } from '@angular/router'
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from '@ngx-formly/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SvgIconComponent } from 'angular-svg-icon'
import { MarkdownPipe } from 'ngx-markdown'

import { ButtonComponent } from '../../components/button/button.component'
import {
  CognitoService,
  ResetPasswordException,
} from '../../services/cognito.service'

export type ForgotPasswordFormModel = {
  username: string
  confirmationCode: string
  passwordValidation: string
  confirmPassword: string
}

@Component({
  selector: 'app-forgot-password',
  imports: [
    ButtonComponent,
    FormlyModule,
    FormsModule,
    SvgIconComponent,
    TranslatePipe,
    RouterLink,
    ReactiveFormsModule,
    MarkdownPipe,
    AsyncPipe,
  ],
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent {
  public loading = false
  public resetError = ''

  public recoveryStartSuccessful: boolean = false
  public resetSuccessful = false

  private lastExecutionTime: number | null = null
  public remainingCooldown: number = -1

  public form = new FormGroup({})
  public options: FormlyFormOptions = {}
  public model: ForgotPasswordFormModel = {
    username: '',
    confirmationCode: '',
    passwordValidation: '',
    confirmPassword: '',
  }
  public fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'flex flex-col gap-8',
      fieldGroup: [
        {
          type: '#email',
          key: 'username',
          props: {
            label: 'pages.passwordReset.email.label',
            required: true,
            recoveryStartSuccessful: false,
          },
          expressions: {
            'props.disabled': (field) => field.props?.recoveryStartSuccessful,
          },
        },
        {
          type: '#code',
          key: 'confirmationCode',
          props: {
            required: true,
            label: 'pages.passwordReset.code.label',
            placeholder: 'pages.passwordReset.code.placeholder',
            recoveryStartSuccessful: false,
          },
          validation: {
            messages: {
              codeMismatch: 'pages.passwordReset.code.validation.codeMismatch',
              expiredCode: 'pages.passwordReset.code.validation.expiredCode',
            },
          },
          expressions: {
            hide: (field) => !field.props?.recoveryStartSuccessful,
          },
        },
        {
          type: '#passwordValidation',
          props: {
            label: 'pages.passwordReset.newPassword.label',
            recoveryStartSuccessful: false,
          },
          validation: {
            messages: {
              passwordHistoryPolicyViolation:
                // eslint-disable-next-line sonarjs/no-hardcoded-passwords
                'pages.passwordReset.newPassword.validation.passwordHistoryPolicyViolation',
            },
          },
          expressions: {
            hide: (field) => !field.props?.recoveryStartSuccessful,
          },
        },
        {
          type: '#password',
          key: 'confirmPassword',
          props: {
            label: 'pages.passwordReset.confirmPassword.label',
            recoveryStartSuccessful: false,
          },
          expressions: {
            hide: (field) => !field.props?.recoveryStartSuccessful,
          },
          validators: {
            fieldMatch: {
              expression: (
                control: AbstractControl,
                field: FormlyFieldConfig
              ): boolean => {
                const passwordValidation =
                  field.parent?.model['passwordValidation']
                const confirmPassword = control.value
                return passwordValidation === confirmPassword
              },
            },
          },
        },
      ],
    },
  ]

  public constructor(
    private readonly cognitoService: CognitoService,
    private readonly translateService: TranslateService
  ) {}

  public async requestCode(): Promise<void> {
    const currentTime = Date.now()
    const cooldownPeriod = 60 * 1000

    if (
      this.lastExecutionTime &&
      currentTime - this.lastExecutionTime < cooldownPeriod
    ) {
      return
    }

    this.loading = true
    const { username } = this.model
    this.cognitoService
      .handleResetPassword(username)
      .then((res) => {
        if (
          res.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE'
        ) {
          this.fields.at(0)?.fieldGroup?.forEach((field) => {
            if (field.props) {
              field.props.recoveryStartSuccessful = true
            }
          })
          this.lastExecutionTime = currentTime
          this.startCooldownTracker(cooldownPeriod)
          this.recoveryStartSuccessful = true
        }
      })
      .catch((err) => console.error(err))
      .finally(() => {
        this.loading = false
      })
  }

  public async confirmReset(): Promise<void> {
    const input = {
      username: this.model['username'],
      confirmationCode: this.model['confirmationCode'],
      newPassword: this.model['passwordValidation'],
      confirmPassword: this.model['confirmPassword'],
    }
    this.cognitoService
      .handleConfirmResetPassword(input)
      .then(() => {
        this.resetSuccessful = true
        this.form.reset()
      })
      .catch((err) => {
        switch (err.name as ResetPasswordException) {
          case 'CodeMismatchException':
            this.form.get('confirmationCode')?.setErrors({ codeMismatch: true })
            break
          case 'ExpiredCodeException':
            this.form.get('confirmationCode')?.setErrors({ expiredCode: true })
            break
          case 'PasswordHistoryPolicyViolationException':
            this.form
              .get('passwordValidation')
              ?.setErrors({ passwordHistoryPolicyViolation: true })
            break
          default:
            this.resetError = this.translateService.instant(
              'validation.genericErrorDetail',
              { errorDetail: err.message }
            )
            break
        }
      })
  }

  private startCooldownTracker(cooldownPeriod: number): void {
    const intervalId = setInterval(() => {
      const elapsedTime = Date.now() - (this.lastExecutionTime ?? 0)
      this.remainingCooldown = Math.ceil((cooldownPeriod - elapsedTime) / 1000)

      if (this.remainingCooldown <= 0) {
        clearInterval(intervalId)
        this.remainingCooldown = 0
      }
    }, 1000)
  }
}
